import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/shop", label: "Shop", testId: "nav-shop-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const linkClass = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-[#111315] text-white" : "text-[#525866] hover:bg-white hover:text-[#111315]"}`;

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#111315]">
      <header className="fixed inset-x-0 top-4 z-50 px-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between rounded-full border border-white/70 bg-white/80 px-4 py-3 shadow-[0_18px_60px_rgba(17,19,21,0.10)] backdrop-blur-xl">
          <Link to="/" data-testid="brand-home-link" aria-label="Operandi Studio home">
            <BrandLogo />
          </Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={item.testId} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
            <a href="/#faq" data-testid="nav-faq-link" className="rounded-full px-4 py-2 text-sm font-semibold text-[#525866] transition-colors hover:bg-white hover:text-[#111315]">
              FAQs
            </a>
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <Button asChild variant="ghost" className="rounded-full" data-testid="nav-owner-login-button">
              <Link to="/admin/login">Owner</Link>
            </Button>
            <Button asChild className="rounded-full bg-[#D97706] px-5 text-white hover:bg-[#B45309]" data-testid="nav-browse-tools-button">
              <Link to="/shop">Browse tools</Link>
            </Button>
          </div>
          <button type="button" data-testid="mobile-menu-button" onClick={() => setOpen((value) => !value)} className="rounded-full p-2 md:hidden" aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="mx-auto mt-3 grid max-w-7xl gap-2 rounded-3xl border border-white/70 bg-white/95 p-4 shadow-xl backdrop-blur-xl md:hidden"
              aria-label="Mobile navigation"
            >
              {[...navItems, { to: "/admin/login", label: "Owner", testId: "mobile-owner-login-link" }].map((item) => (
                <Link key={item.to} to={item.to} data-testid={item.testId} onClick={() => setOpen(false)} className="rounded-2xl px-4 py-3 font-semibold hover:bg-[#F4F0EA]">
                  {item.label}
                </Link>
              ))}
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
      <main>{children}</main>
      <footer className="border-t border-[#E6E2DA] bg-[#111315] text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-[1.2fr_0.8fr_0.8fr]">
          <div>
            <BrandLogo dark />
            <p className="mt-5 max-w-md text-sm leading-7 text-white/62">
              Practical digital products for small-business owners who want clearer marketing, smoother operations, and less repeated admin work.
            </p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-500">Store</p>
            <div className="mt-4 grid gap-3 text-sm text-white/72">
              <Link data-testid="footer-shop-link" to="/shop" className="hover:text-white">Shop</Link>
              <Link data-testid="footer-about-link" to="/about" className="hover:text-white">About</Link>
              <Link data-testid="footer-contact-link" to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-500">Policies</p>
            <div className="mt-4 grid gap-3 text-sm text-white/72">
              <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-white">Privacy</Link>
              <Link data-testid="footer-terms-link" to="/terms" className="hover:text-white">Terms</Link>
              <Link data-testid="footer-refund-link" to="/refund" className="hover:text-white">Refund policy</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 px-6 py-5 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/42">
          © {new Date().getFullYear()} Operandi Studio
        </div>
      </footer>
    </div>
  );
}
